import { ref, watch, readonly, type Component } from 'vue'
import { useRoute } from 'vue-router'
import { LayoutGrid, Headset, FolderKanban, Wrench, Shield, Settings } from 'lucide-vue-next'

const PILLAR_STORAGE_KEY = 'vhq-nav-active-pillar'

export interface NavItem {
  name: string
  path: string
  icon: Component
  permission?: string
  staffOnly?: boolean
  hideForEmployee?: boolean
  clientAdminOnly?: boolean
  superAdminOnly?: boolean
  badge?: 'todo' | 'ticket'
  portalFeature?: string
}

export interface Pillar {
  id: string
  label: string
  icon: Component
  items: NavItem[]
}

// Re-export pillar icons so SideNav can reference them without re-importing
export { LayoutGrid, Headset, FolderKanban, Wrench, Shield, Settings }

// Module-level shared state (singleton)
const activePillarId = ref<string>(localStorage.getItem(PILLAR_STORAGE_KEY) || 'home')
const isPanelOpen = ref<boolean>(false) // default closed — opens on hover

watch(activePillarId, (v) => localStorage.setItem(PILLAR_STORAGE_KEY, v))

export function useNavigation() {
  const route = useRoute()

  // Hover opens the panel, clicking a different pillar switches it
  function selectPillar(id: string) {
    activePillarId.value = id
    isPanelOpen.value = true
  }

  function closePanel() {
    isPanelOpen.value = false
  }

  function openPanel() {
    isPanelOpen.value = true
  }

  // Determine which pillar a given path belongs to
  function pillarForRoute(path: string, pillars: Pillar[]): string | null {
    for (const pillar of pillars) {
      if (pillar.items.some(item =>
        path === item.path || (item.path !== '/' && path.startsWith(item.path))
      )) {
        return pillar.id
      }
    }
    return null
  }

  // Auto-switch active pillar when user navigates to a route in a different pillar
  function syncPillarToRoute(pillars: Pillar[]) {
    const match = pillarForRoute(route.path, pillars)
    if (match && match !== activePillarId.value) {
      activePillarId.value = match
    }
    // Don't auto-open panel on navigation — hover only
  }

  return {
    activePillarId: readonly(activePillarId),
    isPanelOpen: readonly(isPanelOpen),
    selectPillar,
    closePanel,
    openPanel,
    syncPillarToRoute,
    pillarForRoute,
  }
}
